import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { connect } from "react-redux";
import { PageHeader, Select, Spin, message, Drawer, Alert } from "antd";
import { ApartmentOutlined } from "@ant-design/icons";
import { ButtonItem, SelectInsumosItem, SelectItem } from "../../../../Components/Items";
import LienzoMapaWms from "./LienzoMapaWms";
import RackDetalleDrawer from "./RackDetalleDrawer";
import NodoDetalleDrawer from "./NodoDetalleDrawer";

const { Option } = Select;

//Pantalla "Mapa" del módulo WMS: elegir un almacén y ver su mapa (secciones/nodos/racks) de solo lectura -
//doble clic en un rack/nodo/sección abre su detalle (ver RackDetalleDrawer.js/NodoDetalleDrawer.js y el
//<Drawer> inline de sección más abajo); buscar un insumo resalta en qué rack(s) está. El botón
//"Configuración" lleva al editor completo del grafo (MapaAlmacenEditor.js) para el mismo almacén - esta
//pantalla nunca edita el grafo directamente. Reemplaza a la vieja MapaUbicaciones.js (4 pestañas basadas
//en secuenciaRecorrido/numeroRack, que ya no aplican a almacenes con mapa - ver picking.utils.js/
//generarUbicacionesDeRack, que nunca llena esos campos legacy).
const MapaWms = ({
  empresaId,
  headersToken,
  permisoAgregarUbicacion,
  permisoDeshabilitarUbicacion,
  history,
  location,
}) => {
  const [todosProyectos, setTodosProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(undefined);
  const [cargandoMapa, setCargandoMapa] = useState(false);
  const [datosMapa, setDatosMapa] = useState(null);
  const [insumoId, setInsumoId] = useState(undefined);
  const [rackIdsResaltados, setRackIdsResaltados] = useState(new Set());
  const [drawerRack, setDrawerRack] = useState({ visible: false, rackId: null });
  const [drawerNodo, setDrawerNodo] = useState({ visible: false, nodo: null });
  const [drawerSeccion, setDrawerSeccion] = useState({ visible: false, seccion: null });

  const instanciaFlowRef = useRef(null);

  useEffect(() => {
    axios
      .post("proyectos/selectProyectos", { empresaId }, { headers: headersToken })
      .then((res) => {
        if (res.data.status === 404) return;
        setTodosProyectos(res.data || []);
        const proyectoIdPrevio = location?.state?.proyectoId;
        if (proyectoIdPrevio) setProyectoId(proyectoIdPrevio);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarMapa = useCallback(() => {
    if (!proyectoId) {
      setDatosMapa(null);
      return;
    }
    setCargandoMapa(true);
    axios
      .post("mapaAlmacen/selectMapaAlmacen", { empresaId, proyectoId }, { headers: headersToken })
      .then((res) => {
        if (res.data.success === 2) setDatosMapa(res.data);
      })
      .catch((error) => console.error("Error al cargar el mapa:", error))
      .finally(() => setCargandoMapa(false));
  }, [empresaId, headersToken, proyectoId]);

  useEffect(() => {
    setRackIdsResaltados(new Set());
    setInsumoId(undefined);
    cargarMapa();
  }, [cargarMapa]);

  const abrirConfiguracion = () => {
    if (!proyectoId) return;
    history.push("/panel/mapaAlmacenEditor", { proyectoId });
  };

  const onChangeInsumo = async (value) => {
    setInsumoId(value);
    if (!value) {
      setRackIdsResaltados(new Set());
      return;
    }
    try {
      const res = await axios.post(
        "ubicacionProyecto/selectExistenciaPorUbicacionInsumo",
        { empresaId, proyectoId, insumoId: value },
        { headers: headersToken }
      );
      if (res.data.success !== 2) return;

      const idsRacks = new Set(
        (res.data.ubicaciones || []).filter((valor) => valor.rackId).map((valor) => String(valor.rackId))
      );
      setRackIdsResaltados(idsRacks);

      if (idsRacks.size === 0) {
        if (res.data.sinUbicacionAsignada?.cantidad > 0) {
          message.warning("Este insumo tiene existencia sin ubicación asignada en este almacén");
        } else {
          message.info("Este insumo no tiene existencia asignada a ninguna ubicación en este almacén");
        }
        return;
      }

      // Encuadra el primer rack encontrado (mayor existencia, ver selectExistenciaPorUbicacionInsumo) - si
      // hay varios, el usuario puede seguir explorando el resto directamente en el lienzo.
      const primerRackId = idsRacks.values().next().value;
      const rackCrudo = (datosMapa?.racks || []).find((valor) => String(valor._id) === primerRackId);
      if (rackCrudo && instanciaFlowRef.current) {
        instanciaFlowRef.current.fitBounds(
          { x: rackCrudo.x, y: rackCrudo.y, width: rackCrudo.ancho, height: rackCrudo.alto },
          { padding: 0.6, duration: 400 }
        );
      }
    } catch (error) {
      console.error("Error al buscar el insumo:", error);
    }
  };

  const onNodeDoubleClick = ({ tipo, id }) => {
    if (tipo === "rack") {
      setDrawerRack({ visible: true, rackId: id });
    } else if (tipo === "nodo") {
      const nodo = (datosMapa?.nodos || []).find((valor) => String(valor._id) === id);
      setDrawerNodo({ visible: true, nodo });
    } else if (tipo === "seccion") {
      const seccion = (datosMapa?.secciones || []).find((valor) => String(valor._id) === id);
      setDrawerSeccion({ visible: true, seccion });
    }
  };

  const hayMapa = !!datosMapa?.mapaAlmacen;

  return (
    <>
      <PageHeader
        title="Mapa"
        style={{ border: "1px solid rgb(235,237,240)", backgroundColor: "white", marginBottom: 10 }}
        extra={
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <SelectInsumosItem
              empresaId={empresaId}
              headersToken={headersToken}
              value={insumoId}
              onChange={onChangeInsumo}
              width={280}
              placeholder="Buscar insumo..."
              tiposInsumos="inventariables"
              disabled={!proyectoId}
              allowClear
            />
            <SelectItem
              placeholder="Almacén"
              width={200}
              value={proyectoId}
              onChange={(value) => setProyectoId(value)}
            >
              {todosProyectos.map((valor) => (
                <Option value={valor._id} key={valor._id}>
                  {valor.nombre}
                </Option>
              ))}
            </SelectItem>
            <ButtonItem className="botonAgregar" icon={<ApartmentOutlined />} disabled={!proyectoId} onClick={abrirConfiguracion}>
              Configuración
            </ButtonItem>
          </div>
        }
      />

      {!proyectoId ? (
        <div style={{ background: "#fff", borderRadius: 14 }}>
          <div style={{ color: "#999", padding: 20 }}>Selecciona un almacén para ver su mapa.</div>
        </div>
      ) : (
        <Spin spinning={cargandoMapa}>
          {!hayMapa ? (
            <div
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: 40,
                textAlign: "center",
                border: "1px dashed #d9d9d9",
              }}
            >
              <Alert
                type="info"
                showIcon
                message="Este almacén no tiene un mapa configurado todavía"
                description="Configura el mapa (secciones, pasillos y racks) para poder verlo aquí y generar rutas de picking."
                style={{ marginBottom: 16, textAlign: "left" }}
              />
              <ButtonItem type="primary"   className="botonAgregar" icon={<ApartmentOutlined />} onClick={abrirConfiguracion}>
                Configurar mapa
              </ButtonItem>
            </div>
          ) : (
            <LienzoMapaWms
              datosMapa={datosMapa}
              rackIdsResaltados={rackIdsResaltados}
              onNodeDoubleClick={onNodeDoubleClick}
              onInit={(instancia) => {
                instanciaFlowRef.current = instancia;
              }}
              altura="72vh"
            />
          )}
        </Spin>
      )}

      <RackDetalleDrawer
        visible={drawerRack.visible}
        onClose={() => setDrawerRack({ visible: false, rackId: null })}
        rackId={drawerRack.rackId}
        empresaId={empresaId}
        proyectoId={proyectoId}
        headersToken={headersToken}
        permisoAgregarUbicacion={permisoAgregarUbicacion}
        permisoDeshabilitarUbicacion={permisoDeshabilitarUbicacion}
        onConfigurarEnPagina={(rackId) => history.push("/panel/acomodoRack", { rackId, proyectoId })}
      />

      <NodoDetalleDrawer
        visible={drawerNodo.visible}
        onClose={() => setDrawerNodo({ visible: false, nodo: null })}
        nodo={drawerNodo.nodo}
        headersToken={headersToken}
      />

      <Drawer
        title="Detalle de sección"
        visible={drawerSeccion.visible}
        onClose={() => setDrawerSeccion({ visible: false, seccion: null })}
        width={360}
      >
        {drawerSeccion.seccion && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div><strong>Código:</strong> {drawerSeccion.seccion.codigo}</div>
            <div><strong>Nombre:</strong> {drawerSeccion.seccion.nombre}</div>
            <div><strong>Tipo:</strong> {drawerSeccion.seccion.tipo}</div>
            {drawerSeccion.seccion.prioridadPicking != null && (
              <div><strong>Prioridad de picking:</strong> {drawerSeccion.seccion.prioridadPicking}</div>
            )}
            {drawerSeccion.seccion.observaciones && (
              <div><strong>Observaciones:</strong> {drawerSeccion.seccion.observaciones}</div>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  permisoAgregarUbicacion:
    state.user.tipo === 0 ||
    state.user.permisos.find((valueTodos) => valueTodos.permisoId.toString() === "6a5408afe00cff7ab4c38913")?.activado,
  permisoDeshabilitarUbicacion:
    state.user.tipo === 0 ||
    state.user.permisos.find((valueTodos) => valueTodos.permisoId.toString() === "6a5408e0e00cff7ab4c38916")?.activado,
});

export default connect(mapStateToProps)(MapaWms);
